import { useContext, useEffect, useState } from "react";
import CompanyService, {
  DataType,
  Entities
} from "../../../services/CompanyService";
import InvoiceService, {
  getInvoiceFieldDisplayValue
} from "../../../services/InvoiceService";
import ToastContext from "../../shared/bootstrap/Toast";
import Dropdown from "../../shared/components/Dropdown";
import {
  EntityField,
  sortEntityFields
} from "../../shared/entities/EntityField";
import { TranslationService } from "../../../services/TranslationService";
import { InvoiceEditStatus } from "./entities/InvoiceEditStatus";
import { InvoiceGetResponse } from "./entities/InvoiceGetResponse";
import FieldEdit from "./InvoiceMassiveChangesFieldEdit";
import { Cast } from "../../../utils/Utils";
import { RequiredManager } from "../../shared/RequieredManager";
import { formatIntizaDate } from "../../../utils/FormatUtils";
import { parseDate } from "../../../utils/ParseUtils";
import { DefaultValueEnum } from "../../../entities/company/IOStatus/IOStatusEntity";

export const InvoiceDetailStatus = ({
  invoice,
  isEditing,
  afterSubmit,
  editingChanged,
  canEdit = true,
  canEditStatus = true
}: {
  invoice: InvoiceGetResponse.Item;
  isEditing: boolean;
  afterSubmit: () => void;
  editingChanged: (editing: boolean) => void;
  canEdit?: boolean;
  canEditStatus?: boolean;
}) => {
  // Para mostrar el estado actual, usar todos los estados
  const allStatuses = CompanyService.getIoStatus();
  const currentStatus = allStatuses.find(
    (x) => x.IOStatusID === (invoice.IOStatusID ?? 0)
  );
  // Para las opciones del dropdown, usar solo los estados permitidos
  const allowedStatuses = CompanyService.getAllowedInvoiceStatuses();
  const currentStatusId = invoice.IOStatusID ?? 0;
  // Si el estado actual no está en los permitidos, agregarlo para poder mostrarlo
  const statusList = allowedStatuses.find(
    (s) => s.IOStatusID === currentStatusId
  )
    ? [...allowedStatuses]
    : [...(currentStatus ? [currentStatus] : []), ...allowedStatuses];
  const getFields = (statusId: number | undefined): EntityField[] => {
    if (!statusId) {
      return [];
    }
    return CompanyService.getAdditionalDefinitionsFiltered(Entities.Invoice)
      .filter(
        (x) =>
          x.IOStatusDependencies.filter((y) => y.IOStatusID === statusId)
            .length > 0
      )
      .map((x) => new EntityField(x))
      .sort(
        sortEntityFields(
          CompanyService.getIoSortedFields()
            .split(",")
            .map((x) => x.toString())
        )
      );
  };

  const getFieldDependency = (fieldId: number, statusId: number): any => {
    return CompanyService.getAdditionalDefinitionsFiltered(Entities.Invoice)
      .find((x) => x.AdditionalDefinitionID === fieldId)
      ?.IOStatusDependencies.find((y: any) => y.IOStatusID === statusId);
  };

  const getDefaultValue = (
    field: EntityField,
    statusId: number
  ): string | undefined => {
    const dependency = getFieldDependency(field.id, statusId);
    if (!dependency || !(dependency as any).DefaultValue) {
      return undefined;
    }

    const defaultValue = (dependency as any).DefaultValue;
    // Solo establecer fecha actual si hay un DefaultValue configurado para campos de fecha
    if (field.type === DataType.Date && defaultValue) {
      return formatIntizaDate(new Date());
    } else if (
      field.type === DataType.Text &&
      defaultValue === DefaultValueEnum.NombreYApellido
    ) {
      return (
        CompanyService.getUsers()?.find(
          (x) => x.Id === CompanyService.getUserid()
        )?.Value ?? ""
      );
    }
    return undefined;
  };

  const isFieldEditable = (fieldId: number, statusId: number): boolean => {
    const dependency = getFieldDependency(fieldId, statusId);
    if (!dependency) return true; // Por defecto editable si no hay dependencia

    const isEditable = (dependency as any)?.IsEditable;
    // Si es explícitamente false, es readonly
    if (isEditable === false) return false;
    // Si es null o undefined, por defecto es editable (true)
    return true;
  };
  const [fields, setFields] = useState<EntityField[]>(
    getFields(currentStatus?.IOStatusID ?? 0)
  );

  const { showToast } = useContext(ToastContext);
  const { translate } = TranslationService;
  const [isSaving, setIsSaving] = useState(false);
  const [statusEdit, setStatusEdit] = useState(
    new InvoiceEditStatus(
      CompanyService.getUserid(),
      currentStatus?.IOStatusID,
      invoice
    )
  );
  const requiredManager = new RequiredManager();

  useEffect(() => {
    if (!statusEdit.statusId) return;

    // Aplicar valores por defecto para campos no editables o sin valor
    const defaultValues = fields
      .filter((field) => {
        const dependency = getFieldDependency(
          field.id,
          statusEdit.statusId ?? 0
        );
        const hasValue = statusEdit.additionals.find((a) => a.Id === field.id);
        // Aplicar si tiene DefaultValue y no tiene valor actual
        return (dependency as any)?.DefaultValue && !hasValue;
      })
      .map((field) => {
        const defaultValue = getDefaultValue(field, statusEdit.statusId ?? 0);
        return { Id: field.id, Value: defaultValue ?? "" };
      });

    // También formatear fechas existentes (solo si tienen valor)
    const defValues = fields
      .filter((x) => {
        const value = statusEdit.additionals.find((y) => y.Id === x.id)?.Value;
        return (
          x.type === DataType.Date &&
          value &&
          value !== parseDate(value)?.toJSON()
        );
      })
      .map((x) => {
        const value = statusEdit.additionals.find((y) => y.Id === x.id)?.Value;
        const parsedDate = parseDate(value);
        return parsedDate
          ? { Id: x.id, Value: formatIntizaDate(parsedDate) }
          : null;
      })
      .filter((x) => x !== null) as { Id: number; Value: string }[];

    const allDefaultValues = [...defaultValues, ...defValues];
    if (allDefaultValues.length > 0) {
      setStatusEdit((x) => ({
        ...x,
        additionals: [
          ...x.additionals.filter(
            (y) => !allDefaultValues.map((z) => z.Id).includes(y.Id)
          ),
          ...allDefaultValues
        ]
      }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice, statusEdit.statusId]);
  const statusFieldChange = (field: EntityField) => {
    const editable = isFieldEditable(field.id, statusEdit.statusId ?? 0);
    if (!editable) {
      // Si no es editable, no permitir cambios
      return () => {};
    }

    const onChange = (value: string | undefined) => {
      if (value && field.type === DataType.Date) {
        value = formatIntizaDate(new Date(value));
      }
      // No establecer fecha actual como valor por defecto cuando el valor es undefined
      const newAdditionals = [
        ...statusEdit.additionals.filter((x) => x.Id !== field.id),
        { Id: field.id, Value: value ?? "" }
      ];
      setStatusEdit({ ...statusEdit, additionals: newAdditionals });
    };
    const required = CompanyService.getAdditionalDefinitionsFiltered(
      Entities.Invoice
    )
      .find((x) => x.AdditionalDefinitionID === field.id)
      ?.IOStatusDependencies.find(
        (y) => y.IOStatusID === statusEdit.statusId
      )?.Mandatory;
    if (required) {
      return Cast<typeof onChange>(requiredManager.makeRequired(onChange));
    }
    return onChange;
  };

  const statusChange = (statusId?: number) => {
    setStatusEdit(
      new InvoiceEditStatus(CompanyService.getUserid(), statusId, invoice)
    );
    setFields(getFields(statusId ? statusId : undefined));
  };

  const CanSubmit = () => {
    if (statusEdit.statusId === undefined) {
      showToast(translate.SelectStatus, undefined, "warning");
      return false;
    }
    return requiredManager.validate();
  };

  const onSubmit = async () => {
    if (isSaving || !CanSubmit()) {
      return;
    }
    setIsSaving(true);

    const statusEditSave = { ...statusEdit };
    statusEditSave.additionals = statusEditSave.additionals.filter((x) =>
      fields.map((y) => y.id).includes(x.Id)
    );

    // Asegurar que todos los campos con valor por defecto estén en additionals con su valor correcto
    // Especialmente importante para campos no editables que son obligatorios
    const defaultValuesToAdd = fields
      .filter((field) => {
        const dependency = getFieldDependency(
          field.id,
          statusEditSave.statusId ?? 0
        );
        return (dependency as any)?.DefaultValue;
      })
      .map((field) => {
        const defaultValue = getDefaultValue(
          field,
          statusEditSave.statusId ?? 0
        );
        const existingValue = statusEditSave.additionals.find(
          (a) => a.Id === field.id
        );
        // Si no existe en additionals o existe pero está vacío, usar el valor por defecto
        const finalValue =
          !existingValue || !existingValue.Value || existingValue.Value === ""
            ? (defaultValue ?? "")
            : existingValue.Value;
        return { Id: field.id, Value: finalValue };
      });

    // Actualizar los valores por defecto (reemplazar si están vacíos o no existen)
    statusEditSave.additionals = [
      // Mantener los campos que no tienen valor por defecto o que ya tienen un valor válido
      ...statusEditSave.additionals.filter((a) => {
        const hasDefaultValue = defaultValuesToAdd.find((d) => d.Id === a.Id);
        // Si tiene valor por defecto, lo reemplazaremos con el valor por defecto
        // Si no tiene valor por defecto, mantenerlo
        return !hasDefaultValue;
      }),
      // Agregar o actualizar con los valores por defecto
      ...defaultValuesToAdd
    ];

    const result = await InvoiceService.statusChange({
      invoiceIds: [invoice.IOID],
      personId: invoice.personId.toString(),
      statusEdit: statusEditSave
    });
    if (result instanceof Error) {
      showToast(translate.ErrorProcessingRequest, undefined, "danger");
    } else {
      showToast(translate.SavedChanges, undefined, "success");
    }
    setIsSaving(false);
    editingChanged(false);
    afterSubmit && afterSubmit();
  };

  const content = isEditing ? (
    <>
      <div className="d-flex input-row mb-3">
        <label className="form-label">{translate.IOStatus}</label>
        <div className="show-child-on-hover ml-auto">
          <div className="row">
            <div className="col-auto">
              <i
                className="far fa-times text-danger"
                style={{ marginTop: 10 }}
                onClick={() => editingChanged(false)}
              ></i>
              {isSaving && (
                <i className="fas fa-spinner-third fa-spin third ms-2"></i>
              )}
            </div>
            <div className="col-auto">
              <Dropdown
                optionLabel={translate.Select}
                onChange={statusChange}
                items={statusList.map((x) => ({
                  value: x.IOStatusID,
                  text: x.Value
                }))}
                defaultValue={statusEdit.statusId}
              />
            </div>
            <div className="col-auto">
              <button className="btn btn-success" onClick={onSubmit}>
                {translate.Save}
              </button>
            </div>
          </div>
        </div>
      </div>
      {fields.map((field, index) => {
        const editable = isFieldEditable(field.id, statusEdit.statusId ?? 0);
        const defaultValue = getDefaultValue(field, statusEdit.statusId ?? 0);
        const currentValue = statusEdit.additionals.find(
          (a) => a.Id === field.id
        )?.Value;

        return (
          <div className="d-flex input-row mb-3" key={field.id}>
            <label className="form-label">{field.title}</label>
            <div className="ml-auto">
              <FieldEdit
                key={field.id}
                field={field}
                onChange={statusFieldChange(field)}
                status={statusEdit}
                onlyEditor={true}
                invoice={invoice}
                personId={invoice.personId.toString()}
                readOnly={!editable}
                defaultValue={
                  editable ? undefined : currentValue || defaultValue
                }
              />
            </div>
          </div>
        );
      })}
    </>
  ) : (
    <>
      <div className="d-flex input-row mb-3">
        <label className="form-label">{translate.IOStatus}</label>
        <div className="show-child-on-hover ml-auto">
          <div className="row">
            {canEditStatus && CompanyService.canDo("changeiostatus") && (
              <div className="col-auto">
                <i
                  className="pointer hideOnPrint far fa-pen show-when-hovering-parent"
                  onClick={() => editingChanged(true)}
                ></i>
              </div>
            )}
            <div className="col">
              <p className="form-label-detail">{currentStatus?.Value}</p>
              {invoice.PromiseStatusDescription && (
                <span className="form-label-detail labelPromise">
                  {invoice.PromiseStatusDescription}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      {fields.map((field, index) => (
        <div className="d-flex input-row mb-3" key={field.id}>
          <label className="form-label">{field.title}</label>
          <p className="form-label-detail">
            {getInvoiceFieldDisplayValue(field, invoice)}
          </p>
        </div>
      ))}
    </>
  );

  return <>{content}</>;
};

export default InvoiceDetailStatus;
